import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SKYLINE =
  "https://images.unsplash.com/photo-1770706240306-1010c2d321d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBza3lzY3JhcGVyJTIwd2luZG93JTIwdmlldyUyMG5pZ2h0fGVufDB8fHx8MTc3Njc4MzI4NXww&ixlib=rb-4.1.0&q=85";

export default function DealRoom() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    api.get("/deals").then((r) => setDeals(r.data.filter((d) => d.tier_required === "institutional")));
  }, []);

  const isInstitutional = user?.tier === "institutional";

  return (
    <DashboardLayout>
      <div
        className="relative border-b border-border"
        style={{ backgroundImage: `url(${SKYLINE})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-background/88" />
        <div className="relative px-6 md:px-10 py-10">
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 gold-text" strokeWidth={1.5} />
            <span className="overline gold-text">Premium Tier</span>
          </div>
          <h1 className="font-display font-medium text-3xl md:text-5xl tracking-tight mt-3">Deal Room</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-xl">
            Live pipeline reserved for institutional members. Direct introductions, allocation access, monthly private call.
          </p>
        </div>
      </div>

      <div className="px-6 md:px-10 py-8 space-y-4">
        {!isInstitutional && (
          <div className="card-institutional p-8 text-center border-[hsl(var(--gold))]" data-testid="deal-room-locked">
            <Lock className="h-7 w-7 gold-text mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-display font-medium text-2xl">Access by Application Only</h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
              Current tier: <span className="capitalize">{user?.tier || "standard"}</span>. Institutional members deploy typical tickets of $1M+ per deal.
            </p>
            <button onClick={() => navigate("/pricing")} className="btn-primary mt-5" data-testid="deal-room-apply">
              Apply for Access <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {deals.map((d) => (
          <div key={d.id} className="card-institutional p-6 grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid={`deal-room-deal-${d.id}`}>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="overline">{d.country}</span>
                <StatusBadge status={d.status} />
              </div>
              <h3 className="font-display font-medium text-xl">{d.title}</h3>
              <div className="flex flex-wrap gap-5 text-xs mt-3">
                <div><span className="text-muted-foreground">Size</span> <span className="font-mono ml-2">{d.size_usd}</span></div>
                <div><span className="text-muted-foreground">Return</span> <span className="font-mono ml-2 gold-text">{d.return_text}</span></div>
                <div><span className="text-muted-foreground">Min Ticket</span> <span className="font-mono ml-2">{d.min_ticket}</span></div>
                <div><span className="text-muted-foreground">Timeline</span> <span className="font-mono ml-2">{d.timeline}</span></div>
              </div>
            </div>
            <button
              onClick={() => navigate(`/deals/${d.id}`)}
              className={isInstitutional ? "btn-primary" : "btn-ghost"}
              data-testid={`view-deal-room-deal-${d.id}`}
            >
              {isInstitutional ? "Request Introduction" : "Preview"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
